/** See docs/authz.md Usage → Gate a route. */
import { type PermissionSet, type Principal, can } from "@features/authz/can";

import type { protectedRoute } from "./types";

function filterRoute(
  route: protectedRoute,
  principal: Principal,
  permissions: PermissionSet,
  inheritedAllowedRoles?: readonly Principal["role"][]
): protectedRoute | null {
  if (route.permissionId) {
    if (!can(permissions, route.permissionId)) {
      return null;
    }
  } else {
    const allowedRoles = route.allowedRoles ?? inheritedAllowedRoles;
    if (allowedRoles && !allowedRoles.includes(principal.role)) {
      return null;
    }
  }

  if (route.index === true) {
    return route;
  }

  const children = route.children?.flatMap((child) => {
    const filtered = filterRoute(
      child,
      principal,
      permissions,
      route.defaultChildrenAllowedRoles
    );
    return filtered ? [filtered] : [];
  });

  return children ? { ...route, children } : route;
}

/**
 * Drops routes the principal cannot access. `permissionId` is the only gate
 * for those routes (`can()`; no inherited role buckets). Others keep
 * `allowedRoles` / child role inheritance, which is why `principal` is still
 * required alongside the resolved permission set.
 */
export function filterRoutesForAccess(
  routes: readonly protectedRoute[],
  principal: Principal,
  permissions: PermissionSet
): protectedRoute[] {
  return routes.flatMap((route) => {
    const filtered = filterRoute(route, principal, permissions);
    return filtered ? [filtered] : [];
  });
}
